import React from 'react';
import PropTypes from 'prop-types';

import Icon from '@avtechsoftware/react-component-library/src/components/common/Icon/Icon';
import style from './EditableTextField.module.css';

class EditableTextField extends React.Component {
  constructor(props) {
    super(props);
    this.titleWidth = 0;
    this.titleDisplayRef = React.createRef();
    this.inputRef = React.createRef();
    this.updateTitleOnInputChange = this.updateTitleOnInputChange.bind(this);
    this.saveChangeOrCancelByKey = this.saveChangeOrCancelByKey.bind(this);
    this.startEditing = this.startEditing.bind(this);
    this.saveAndStopEditing = this.saveAndStopEditing.bind(this);
    const { originalTitle } = this.props;
    this.state = {
      isEditing: false,
      newTitle: originalTitle,
    };
  }

  componentDidMount() {
    this.trackTitleWidth();
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { originalTitle } = nextProps;
    const { isEditing } = prevState;

    // Compare nextProps with previous props (or current state) to determine if state needs updating
    if (!isEditing) {
      // Return an object to update the state
      return {
        ...prevState,
        newTitle: originalTitle,
      };
    }
    // Return null if no state update is needed
    return null;
  }

  componentDidUpdate() {
    this.trackTitleWidth();
  }

  trackTitleWidth() {
    const { isEditing } = this.state;
    if (!isEditing && this.titleDisplayRef.current) {
      const currentWidth = this.titleDisplayRef.current.offsetWidth;
      const minWidth = 200;
      this.titleWidth = currentWidth > minWidth ? currentWidth : minWidth;
    }
  }

  startEditing() {
    this.setState({
      isEditing: true,
    });
  }

  saveNewTitle() {
    const { newTitle } = this.state;
    const { originalTitle, saveTitle, allowEmptyTitle } = this.props;

    if (newTitle === '' && allowEmptyTitle) {
      saveTitle(newTitle);

      this.setState({
        isEditing: false,
      });

      return;
    }

    if (newTitle) {
      if (originalTitle !== newTitle) {
        saveTitle(newTitle);
      }
    } else {
      this.revertTitle();
    }

    this.setState({
      isEditing: false,
    });
  }

  revertTitle() {
    const { originalTitle } = this.props;
    this.setState({ newTitle: originalTitle });
  }

  updateTitleOnInputChange(e) {
    this.setState({
      newTitle: e.target.value.trim(),
    });
  }

  saveChangeOrCancelByKey(e) {
    switch (e.key) {
      case 'Enter':
        this.saveNewTitle();
        this.setState({ isEditing: false });
        break;
      case 'Escape':
        this.revertTitle();
        this.setState({ isEditing: false });
        break;
      default:
        break;
    }
  }

  saveAndStopEditing() {
    this.saveNewTitle();
    this.setState({ isEditing: false });
  }

  render() {
    const {
      userHasTitleEditPrivileges,
      fontSize,
      noPadding,
      originalTitle,
    } = this.props;
    const { isEditing, newTitle } = this.state;

    const customStyle = {
      ...(fontSize ? { fontSize: `${fontSize}rem` } : {}),
      ...(noPadding ? { padding: 0 } : {}),
    };

    return (
      <div className="form-inline">
        { !isEditing
          ? (
            <>
              {userHasTitleEditPrivileges
                ? (
                  <>
                    <button
                      ref={this.titleDisplayRef}
                      type="button"
                      className={`btn btn-lg ${style.titleDisplay}`}
                      aria-label="Edit"
                      title="Edit"
                      id="editable-title"
                      onClick={this.startEditing}
                      style={customStyle}
                    >
                      {originalTitle}
                      {' '}
                      <small className={`text-muted ${style.editIcon}`}><Icon icon="pen" /></small>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className={`btn btn-lg ${style.titleDisplayUnclickable}`}
                    style={customStyle}
                  >
                    {originalTitle}
                  </button>
                )}
            </>
          )
          : (
            <input
              style={{ width: this.titleWidth, ...customStyle }}
              className="form-control form-control-lg"
              ref={this.inputRef}
              type="text"
              defaultValue={newTitle}
              /* eslint-disable-next-line jsx-a11y/no-autofocus */
              autoFocus
              onChange={this.updateTitleOnInputChange}
              onBlur={this.saveAndStopEditing}
              onKeyDown={this.saveChangeOrCancelByKey}
            />
          )}
      </div>
    );
  }
}

EditableTextField.defaultProps = {
  fontSize: '',
  noPadding: false,
  allowEmptyTitle: false,
};

EditableTextField.propTypes = {
  originalTitle: PropTypes.string.isRequired,
  userHasTitleEditPrivileges: PropTypes.bool.isRequired,
  saveTitle: PropTypes.func.isRequired,
  fontSize: PropTypes.number,
  noPadding: PropTypes.bool,
  allowEmptyTitle: PropTypes.bool,
};

export default EditableTextField;
